import React, { useEffect, useMemo, useRef, useState } from "react";
import type { TChatRoom, TUserRef } from "@/redux/feature/chat/chatType";
import type { TChatMessage } from "@/pages/messaging/ChatWindow";
import WarningIcon from "@/assets/icons/WarningIcon.svg";

export type OrderChatPanelProps = {
  collapsed?: boolean;
  onCollapsedChange?: (next: boolean) => void;
  onCollapse?: () => void;

  room?: TChatRoom | null;
  headerUserOverride?: TUserRef | null;

  myUserId: string;
  myRole: "client" | "creator";

  showSecurityReminder?: boolean;
  showHeader?: boolean;
  borderless?: boolean;
  bodyClassName?: string;

  messages: TChatMessage[];
  loading?: boolean;

  disabled?: boolean;
  closedNotice?: string;
  placeholder?: string;

  onSend: (text: string) => void;
  onReachTop?: () => void;

  statusDot?: "online" | "offline" | "away";
  meta?: string;

  avatarUrl?: string;
  headerNameOverride?: string;
};

const cn = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(" ");

const formatTime = (date: Date) =>
  date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

function Bubble({
  mine,
  text,
  time,
  senderName,
  warningText,
}: {
  mine: boolean;
  text: string;
  time?: string;
  senderName?: string;
  warningText?: string;
}) {
  return (
    <div className={cn("flex", mine ? "justify-end" : "justify-start")}>
      <div className="max-w-[82%]">
        {!mine && senderName ? (
          <div className="mb-1 pl-1 text-xs font-medium text-[#667085]">
            {senderName}
          </div>
        ) : null}

        <div
          className={cn(
            "rounded-[16px] px-4 py-3 text-[14px] leading-5 shadow-[0_1px_2px_rgba(16,24,40,0.06)]",
            mine
              ? "rounded-br-[6px] bg-[#0F172A] text-white"
              : "rounded-bl-[6px] border border-[#EAECF0] bg-white text-[#101828]",
          )}
        >
          {text}
        </div>
        {warningText ? (
          <div
            className={cn(
              "mt-1 px-1 text-[11px] text-[#B42318]",
              mine ? "text-right" : "text-left",
            )}
          >
            {warningText}
          </div>
        ) : null}

        {time ? (
          <div
            className={cn(
              "mt-1 px-1 text-[11px] text-[#98A2B3]",
              mine ? "text-right" : "text-left",
            )}
          >
            {time}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function getOtherUser(room: TChatRoom, myRole: "client" | "creator") {
  return myRole === "client" ? room.creator_id : room.client_id;
}

function SecurityReminder() {
  return (
    <div className="mb-3 flex items-start gap-2 rounded-xl border border-[#EAECF0] bg-white px-3 py-2.5">
      <div className="mt-0.5 flex shrink-0 items-center">
        <WarningIcon />
      </div>
      <p className="text-[10px] leading-[15px] text-[#667085]">
        Sharing contact details is against platform policy. Use this chat only
        for platform-related communication.
      </p>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex h-full items-center justify-center px-6 text-center">
      <div className="max-w-[280px]">
        <p className="text-[10px] leading-5 text-[#667085]">
          This is the beginning of your conversation. Please communicate only
          through this chat. Sharing contact details is against platform policy.
          This chat is{" "}
          <span className="font-semibold text-[#475467]">moderated</span> and
          may be recorded for training.
        </p>
      </div>
    </div>
  );
}

export default function OrderChatPanel({
  collapsed = false,
  onCollapsedChange,
  onCollapse,
  room,
  headerUserOverride,
  myUserId,
  myRole,
  showSecurityReminder = true,
  showHeader = true,
  borderless = false,
  bodyClassName,
  messages,
  loading = false,
  disabled = false,
  closedNotice,
  placeholder = "Type your message here",
  onSend,
  onReachTop,
  statusDot = "offline",
  meta,
  avatarUrl,
  headerNameOverride,
}: OrderChatPanelProps) {
  const [draft, setDraft] = useState("");
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const didInitialScrollRef = useRef(false);

  useEffect(() => {
    didInitialScrollRef.current = false;
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = 0;
    }
  }, [room?.room_id]);

  const headerUser: TUserRef | null = useMemo(() => {
    if (headerUserOverride) return headerUserOverride;
    if (!room) return null;
    return getOtherUser(room, myRole);
  }, [headerUserOverride, room, myRole]);

  const headerName = headerNameOverride || headerUser?.fullName || "Messages";
  const canSend = !disabled && draft.trim().length > 0;

  const scrollToBottom = (behavior: ScrollBehavior = "auto") => {
    if (!scrollerRef.current) return;
    const el = scrollerRef.current;
    requestAnimationFrame(() => {
      el.scrollTo({
        top: el.scrollHeight,
        behavior,
      });
    });
  };

  useEffect(() => {
    if (!loading && !didInitialScrollRef.current) {
      scrollToBottom("auto");
      didInitialScrollRef.current = true;
    }
  }, [loading, messages.length]);

  useEffect(() => {
    if (!scrollerRef.current) return;
    const el = scrollerRef.current;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < 220) {
      scrollToBottom("auto");
    }
  }, [messages.length]);

  const handleSend = () => {
    const text = draft.trim();
    if (!text) return;
    onSend(text);
    setDraft("");
    setTimeout(() => scrollToBottom("smooth"), 30);
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (canSend) handleSend();
    }
  };

  const handleScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
    if (!onReachTop) return;
    if (e.currentTarget.scrollTop <= 8) onReachTop();
  };

  const toggleCollapse = () => {
    if (onCollapsedChange) return onCollapsedChange(!collapsed);
    if (onCollapse) return onCollapse();
  };

  return (
    <section
      className={cn(
        "relative h-full min-h-0 w-full transition-all duration-300 ease-out",
        collapsed ? "opacity-0 pointer-events-none" : "opacity-100",
      )}
      aria-hidden={collapsed}
    >
      <div
        className={cn(
          "flex h-full min-h-0 flex-col overflow-hidden bg-white",
          borderless
            ? "rounded-none border-0 shadow-none"
            : "rounded-2xl border border-[#EAECF0] shadow-[0_1px_2px_rgba(16,24,40,0.06)]",
        )}
      >
        {showHeader ? (
          <div className="flex items-center justify-between gap-3 border-b border-[#EAECF0] px-4 py-4">
            <div className="flex min-w-0 items-center gap-3">
              <div className="relative shrink-0">
                <img
                  src={avatarUrl || "https://via.placeholder.com/48x48"}
                  alt={headerName}
                  className="h-11 w-11 rounded-full object-cover"
                />
                <span
                  className={cn(
                    "absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2 border-white",
                    statusDot === "online"
                      ? "bg-[#22C55E]"
                      : statusDot === "away"
                        ? "bg-[#F59E0B]"
                        : "bg-[#98A2B3]",
                  )}
                />
              </div>

              <div className="min-w-0">
                <div className="truncate text-[15px] font-semibold leading-6 text-[#101828]">
                  {headerName}
                </div>
                {meta ? (
                  <div className="mt-0.5 truncate text-[13px] text-[#475467]">
                    {meta}
                  </div>
                ) : null}
              </div>
            </div>

            <button
              onClick={toggleCollapse}
              className="inline-flex items-center gap-1 rounded-full border border-[#D0D5DD] bg-white px-2 py-1.5 text-xs font-medium text-[#111827] hover:bg-[#F9FAFB] focus:outline-none focus:ring-2 focus:ring-[#D0D5DD]"
              type="button"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M14 7L9 12L14 17"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M20 7L15 12L20 17"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>{collapsed ? "Expand" : "Collapse"}</span>
            </button>
          </div>
        ) : null}

        <div
          className={cn(
            "flex min-h-0 flex-1 flex-col bg-[#F8F8F8]",
            bodyClassName,
          )}
        >
          <div
            ref={scrollerRef}
            onScroll={handleScroll}
            className="min-h-0 flex-1 overflow-y-auto px-4 py-4"
          >
            {showSecurityReminder ? <SecurityReminder /> : null}

            {loading ? (
              <div className="space-y-3">
                <div className="h-10 w-[70%] animate-pulse rounded-2xl bg-[#F2F4F7]" />
                <div className="ml-auto h-10 w-[55%] animate-pulse rounded-2xl bg-[#F2F4F7]" />
                <div className="h-10 w-[62%] animate-pulse rounded-2xl bg-[#F2F4F7]" />
              </div>
            ) : messages.length === 0 ? (
              <EmptyState />
            ) : (
              <div className="space-y-4">
                {messages.map((m) => {
                  const mine = m.sender === myRole;
                  return (
                    <Bubble
                      key={m.id}
                      mine={mine}
                      text={m.content}
                      warningText={m.isBlocked ? m.warningText : undefined}
                      senderName={mine ? undefined : m.senderName}
                      time={formatTime(m.timestamp)}
                    />
                  );
                })}
              </div>
            )}
          </div>

          <div className="border-t border-[#EAECF0] bg-white p-3">
            {closedNotice ? (
              <p className="text-center text-xs text-[#667085]">{closedNotice}</p>
            ) : (
            <div className="flex items-center gap-2">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                disabled={disabled}
                rows={1}
                className={cn(
                  "max-h-28 min-h-[48px] flex-1 resize-none rounded-2xl border border-[#EAECF0] bg-white px-4 py-3",
                  "text-[15px] text-[#101828] placeholder:text-[#98A2B3]",
                  "focus:outline-none focus:ring-2 focus:ring-[#D0D5DD]",
                  disabled && "opacity-60",
                )}
              />

              <button
                type="button"
                onClick={handleSend}
                disabled={!canSend}
                className={cn(
                  "inline-flex h-12 min-w-[72px] items-center justify-center rounded-2xl px-4 text-sm font-semibold transition-colors",
                  canSend
                    ? "bg-[#FF6A00] text-white"
                    : "bg-[#F2F4F7] text-[#98A2B3]",
                )}
              >
                Send
              </button>
            </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
